"use client";

import * as React from "react";
import {
  USLandModal,
  ToolbarButtonProps,
} from "@/components/usland-modal/usland-modal";
import { Button } from "@/components/ui/button";
import { X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Unified Modal System
 *
 * Fully refactored to replace legacy TRON properties with Priscilla's unified 
 * .usland-glass utility framework and semantic design tokens.
 */

/**
 * Modal action button configuration
 */
export interface ModalAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

/**
 * Unified Modal Props
 */
export interface UnifiedModalProps {
  // Required
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;

  // Content
  children: React.ReactNode;
  description?: string;

  // Actions
  primaryAction?: ModalAction;
  secondaryAction?: ModalAction;
  cancelAction?: ModalAction;
  showCloseButton?: boolean;

  // Sizing
  size?: "sm" | "md" | "lg" | "xl" | "full";
  maxWidth?: string;

  // TRON Theme Effects (Kept for backward compatibility interface shapes)
  showGridOverlay?: boolean;
  enableGlow?: boolean;

  // State
  loading?: boolean;
  error?: string;

  // Z-index (for modal stacking)
  zIndex?: string | number;

  // Styling
  className?: string;
  contentClassName?: string;
}

/**
 * Size configurations
 */
const sizeClasses = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  full: "max-w-full mx-4",
};

/**
 * Unified Modal Component
 */
export function UnifiedModal({
  open,
  onOpenChange,
  title,
  children,
  description,
  primaryAction,
  secondaryAction,
  cancelAction,
  showCloseButton = true,
  size = "lg",
  maxWidth,
  showGridOverlay = false,
  enableGlow = false,
  error,
  loading = false,
  zIndex = "var(--z-modal)",
  className,
  contentClassName,
}: UnifiedModalProps) {
  // Build toolbar buttons from actions
  const toolbarButtonsRight: ToolbarButtonProps[] = [];

  // Primary action using core brand buttons
  if (primaryAction) {
    toolbarButtonsRight.push({
      label: primaryAction.label,
      onClick: primaryAction.onClick,
      variant: "primary",
      disabled: primaryAction.disabled || primaryAction.loading || loading,
      className: cn(
        "bg-[rgb(var(--primary-default))] text-white border-0 hover:bg-[rgb(var(--primary-hover))] transition-colors rounded-md font-bold text-xs px-4 h-9 shadow-sm",
        primaryAction.variant === "danger" && "bg-red-600 hover:bg-red-700 text-white"
      ),
    });
  }

  // Secondary action
  if (secondaryAction) {
    toolbarButtonsRight.push({
      label: secondaryAction.label,
      onClick: secondaryAction.onClick,
      variant: "default",
      disabled: secondaryAction.disabled || loading,
      className: "border-gray-200 bg-white/60 text-gray-700 hover:bg-white font-bold text-xs h-9 rounded-md transition-colors"
    });
  }

  // Cancel action (or default close)
  if (cancelAction) {
    toolbarButtonsRight.push({
      label: cancelAction.label,
      onClick: cancelAction.onClick,
      variant: "default",
      disabled: cancelAction.disabled || loading,
      className: "border-gray-200 bg-white/40 text-gray-500 hover:bg-white/80 font-bold text-xs h-9 rounded-md transition-colors"
    });
  }

  return (
    <USLandModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      showDefaultClose={showCloseButton && !cancelAction}
      toolbarButtonsRight={
        toolbarButtonsRight.length > 0 ? toolbarButtonsRight : undefined
      }
      zIndex={zIndex}
      className={cn(
        "max-h-[90vh] flex flex-col border usland-glass shadow-2xl rounded-lg",
        sizeClasses[size],
        maxWidth && maxWidth,
        className
      )}
      contentClassName={cn("flex flex-col p-6", contentClassName)}
    >
      {/* Description */}
      {description && (
        <p className="text-gray-500 font-semibold text-sm mb-4">{description}</p>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-900">{error}</p>
        </div>
      )}

      {/* Loading overlay configured to strict design layer stacks */}
      {loading && (
        <div className="absolute inset-0 z-[--z-modal] flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(var(--primary-default))]" />
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Processing Cache...</p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="min-h-0 flex-1 text-gray-700 font-medium text-sm">{children}</div>
    </USLandModal>
  );
}

/**
 * Modal Footer Component
 */
export interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
  align?: "left" | "center" | "right" | "space-between";
}

export function ModalFooter({
  children,
  className,
  align = "right",
}: ModalFooterProps) {
  const alignClasses = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
    "space-between": "justify-between",
  };

  return (
    <div
      className={cn(
        "mt-4 flex items-center gap-2 border-t border-gray-200/60 pt-4",
        alignClasses[align],
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Modal Header Component
 */
export interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function ModalHeader({ children, className }: ModalHeaderProps) {
  return <div className={cn("mb-4", className)}>{children}</div>;
}

/**
 * Modal Body Component
 */
export interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function ModalBody({ children, className }: ModalBodyProps) {
  return (
    <div className={cn("-mx-6 min-h-0 flex-1 overflow-y-auto px-6", className)}>
      {children}
    </div>
  );
}

/**
 * Quick Action Modal
 */
export interface QuickActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm?: () => void;
  cancelLabel?: string;
  variant?: "default" | "danger" | "warning";
  zIndex?: string | number;
}

export function QuickActionModal({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel = "Confirm",
  onConfirm,
  cancelLabel = "Cancel",
  variant = "default",
  zIndex = "var(--z-modal)",
}: QuickActionModalProps) {
  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  return (
    <UnifiedModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      zIndex={zIndex}
      primaryAction={{
        label: confirmLabel,
        onClick: handleConfirm,
        variant: variant === "danger" ? "danger" : "primary",
      }}
      cancelAction={{
        label: cancelLabel,
        onClick: () => onOpenChange(false),
        variant: "ghost",
      }}
      showCloseButton={false}
    >
      <p className="text-sm font-semibold leading-relaxed text-gray-600">{message}</p>
    </UnifiedModal>
  );
}

/**
 * Confirm Dialog
 */
export function confirmDialog(options: {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger" | "warning";
}): Promise<boolean> {
  return new Promise((resolve) => {
    resolve(false);
  });
}

export default UnifiedModal;